import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import RemoveIcon from '@mui/icons-material/Remove';
import { Box, Card, CardContent, Typography } from '@mui/material';
import type { ApiOverviewResource } from '@repo/api-contract';
import { OverviewMiniChart } from './OverviewMiniChart';

type ResourceConfig = {
  label: string;
  color: string;
};

type OverviewStatCardProps = {
  resource: ApiOverviewResource;
  config: ResourceConfig;
  periodLabel: string;
  createdSuffix: string;
  chartCountLabel: string;
};

function getTrend(buckets: ApiOverviewResource['buckets']) {
  if (buckets.length < 2) {
    return { percent: null as number | null, direction: 'flat' as const };
  }

  const previous = buckets[buckets.length - 2]?.value ?? 0;
  const current = buckets[buckets.length - 1]?.value ?? 0;

  if (previous === 0) {
    if (current === 0) {
      return { percent: 0, direction: 'flat' as const };
    }
    return { percent: 100, direction: 'up' as const };
  }

  const percent = ((current - previous) / previous) * 100;

  if (percent > 0) {
    return { percent, direction: 'up' as const };
  }
  if (percent < 0) {
    return { percent, direction: 'down' as const };
  }
  return { percent: 0, direction: 'flat' as const };
}

export function OverviewStatCard({
  resource,
  config,
  periodLabel,
  createdSuffix,
  chartCountLabel,
}: OverviewStatCardProps) {
  const trend = getTrend(resource.buckets);
  const TrendIcon =
    trend.direction === 'up'
      ? ArrowUpwardIcon
      : trend.direction === 'down'
        ? ArrowDownwardIcon
        : RemoveIcon;

  return (
    <Card
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
        height: '100%',
      }}
    >
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Typography
          variant="subtitle1"
          sx={{
            color: config.color,
            fontWeight: 600,
            mb: 0.5,
            lineHeight: 1.4,
          }}
        >
          {config.label}
        </Typography>
        <Typography variant="subtitle1" sx={{ mb: 1.5, lineHeight: 1.4 }}>
          <Box component="span" fontWeight={700} color="text.primary">
            {resource.totalPeriod.toLocaleString()}
          </Box>{' '}
          <Box component="span" color="text.secondary" fontWeight={400}>
            {createdSuffix}
          </Box>
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            mt: 1.5,
            mb: 2,
            color: config.color,
          }}
        >
          <TrendIcon sx={{ fontSize: 16 }} />
          <Typography
            variant="body2"
            sx={{ color: 'inherit', fontWeight: 600 }}
          >
            {trend.percent === null
              ? '—'
              : `${Math.abs(trend.percent).toFixed(1)}%`}{' '}
            {periodLabel}
          </Typography>
        </Box>
        <OverviewMiniChart
          buckets={resource.buckets}
          color={config.color}
          countLabel={chartCountLabel}
        />
      </CardContent>
    </Card>
  );
}
